import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Waves, Shield, BatteryCharging, Orbit } from "lucide-react";
import Reveal from "./Reveal";

const cardBase =
    "group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-7 backdrop-blur-sm transition-all duration-500 hover:border-white/25 hover:bg-white/[0.05]";

function Chip({ children }) {
    return (
        <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">
            {children}
        </span>
    );
}

function EqVisualizer() {
    const [bars, setBars] = useState(() =>
        Array.from({ length: 28 }, () => 25 + Math.random() * 55),
    );
    useEffect(() => {
        const id = setInterval(
            () =>
                setBars(
                    Array.from({ length: 28 }, () => 15 + Math.random() * 85),
                ),
            480,
        );
        return () => clearInterval(id);
    }, []);
    return (
        <div
            data-testid="nexora-sound-visualizer"
            className="flex h-36 items-end gap-[4px] md:h-44"
            aria-hidden="true"
        >
            {bars.map((h, i) => (
                <motion.div
                    key={i}
                    className="flex-1 rounded-full bg-gradient-to-t from-laser/50 via-volt/50 to-volt"
                    animate={{ height: `${h}%` }}
                    transition={{ type: "spring", stiffness: 170, damping: 22 }}
                    style={{ opacity: 0.55 + (i / 28) * 0.45 }}
                />
            ))}
        </div>
    );
}

function AncRings() {
    return (
        <div className="relative flex h-28 w-28 items-center justify-center">
            {[0, 1.3].map((d) => (
                <motion.span
                    key={d}
                    className="absolute inset-0 rounded-full border border-laser/50"
                    animate={{ scale: [1, 1.7], opacity: [0.65, 0] }}
                    transition={{
                        duration: 2.6,
                        repeat: Infinity,
                        delay: d,
                        ease: "easeOut",
                    }}
                />
            ))}
            <span className="absolute inset-3 rounded-full bg-[radial-gradient(circle,rgba(112,0,255,0.28),transparent_70%)]" />
            <Shield className="h-7 w-7 text-hyper" />
        </div>
    );
}

function ChargeBar() {
    return (
        <div className="w-full">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                <motion.div
                    className="h-full rounded-full bg-gradient-to-r from-volt to-hyper shadow-[0_0_14px_rgba(0,240,255,0.6)]"
                    initial={{ width: "0%" }}
                    whileInView={{ width: "100%" }}
                    viewport={{ once: true, amount: 0.6 }}
                    transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
                />
            </div>
            <div className="mt-3 flex justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-white/45">
                <span>10 min charge</span>
                <span>8 hrs playback</span>
            </div>
        </div>
    );
}

function wavePath(amp, phase) {
    let d = "M0 60";
    for (let x = 8; x <= 640; x += 8) {
        const y = 60 - Math.sin((x / 640) * Math.PI * 5 + phase) * amp;
        d += ` L${x} ${y.toFixed(1)}`;
    }
    return d;
}

function SpatialWave() {
    const [v, setV] = useState(60);
    const amp = (v / 100) * 42;
    return (
        <div className="flex-1">
            <svg
                viewBox="0 0 640 120"
                preserveAspectRatio="none"
                className="h-24 w-full md:h-28"
                aria-hidden="true"
            >
                <defs>
                    <linearGradient id="waveg" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0" stopColor="#00F0FF" />
                        <stop offset="1" stopColor="#A855F7" />
                    </linearGradient>
                </defs>
                <line
                    x1="0"
                    y1="60"
                    x2="640"
                    y2="60"
                    stroke="rgba(255,255,255,0.08)"
                    strokeDasharray="4 6"
                />
                <path
                    d={wavePath(amp * 0.5, 0.9)}
                    fill="none"
                    stroke="url(#waveg)"
                    strokeOpacity="0.35"
                    strokeWidth="1.5"
                />
                <path
                    d={wavePath(amp, 0)}
                    fill="none"
                    stroke="url(#waveg)"
                    strokeWidth="2"
                    strokeLinecap="round"
                />
            </svg>
            <div className="mt-4 flex items-center gap-4">
                <input
                    type="range"
                    min="10"
                    max="100"
                    value={v}
                    aria-label="Field intensity"
                    onChange={(e) => setV(Number(e.target.value))}
                    className="nexora-slider"
                    style={{ ["--fill"]: `${v}%` }}
                />
                <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.2em] text-volt">
                    Field {v}%
                </span>
            </div>
        </div>
    );
}

export default function Exploration() {
    return (
        <section id="technology" className="relative py-28 md:py-36">
            <div
                aria-hidden="true"
                className="pointer-events-none absolute left-1/2 top-0 h-[28rem] w-[60rem] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse,rgba(112,0,255,0.09),transparent_65%)]"
            />
            <div className="relative mx-auto max-w-7xl px-6 md:px-10">
                <Reveal className="mb-14 flex flex-col justify-between gap-6 md:flex-row md:items-end">
                    <div>
                        <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.35em] text-cyan-300/80 md:text-xs">
                            02 — Inside the machine
                        </p>
                        <h2 className="font-display text-4xl font-extrabold uppercase tracking-tight text-white md:text-6xl">
                            Engineered
                            <br />
                            for the void.
                        </h2>
                    </div>
                    <p className="max-w-xs text-sm font-light leading-relaxed text-white/50 md:text-base">
                        Every element exists to disappear — leaving nothing
                        between you and the signal.
                    </p>
                </Reveal>

                <div
                    data-testid="nexora-bento-grid"
                    className="grid grid-cols-1 gap-5 md:grid-cols-12"
                >
                    <Reveal
                        data-testid="nexora-bento-item-driver"
                        className={`md:col-span-7 md:row-span-2 ${cardBase}`}
                    >
                        <div className="flex h-full flex-col">
                            <div className="mb-5 flex items-center justify-between">
                                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-volt/80">
                                    01 / Acoustic Architecture
                                </span>
                                <Waves className="h-5 w-5 text-white/30" />
                            </div>
                            <h3 className="font-display text-2xl font-bold text-white md:text-3xl">
                                50mm Graphene drivers.
                            </h3>
                            <p className="mt-3 max-w-md text-sm font-light leading-relaxed text-white/55 md:text-base">
                                Custom 50mm Graphene diaphragms delivering a
                                4Hz – 48kHz frequency response with zero
                                distortion.
                            </p>
                            <div className="mt-8 flex-1">
                                <EqVisualizer />
                            </div>
                            <div className="mt-8 flex flex-wrap gap-2">
                                <Chip>50mm Graphene</Chip>
                                <Chip>4Hz – 48kHz</Chip>
                                <Chip>Zero Distortion</Chip>
                            </div>
                        </div>
                    </Reveal>

                    <Reveal
                        data-testid="nexora-bento-item-anc"
                        delay={0.08}
                        className={`md:col-span-5 ${cardBase}`}
                    >
                        <div className="flex items-start justify-between gap-6">
                            <div>
                                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-volt/80">
                                    02 / Quantum ANC™
                                </span>
                                <h3 className="mt-3 font-display text-xl font-bold text-white md:text-2xl">
                                    Silence, engineered.
                                </h3>
                                <p className="mt-3 text-sm font-light leading-relaxed text-white/55">
                                    Active noise cancellation sampling at
                                    192kHz with real-time room geometry
                                    mapping.
                                </p>
                            </div>
                            <AncRings />
                        </div>
                        <div className="mt-6 flex items-baseline gap-3">
                            <span className="font-display text-4xl font-extrabold text-hyper md:text-5xl">
                                −45dB
                            </span>
                            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                                isolation
                            </span>
                        </div>
                    </Reveal>

                    <Reveal
                        data-testid="nexora-bento-item-battery"
                        delay={0.16}
                        className={`md:col-span-5 ${cardBase}`}
                    >
                        <div className="flex items-center justify-between">
                            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-volt/80">
                                03 / Hyper-Charge
                            </span>
                            <BatteryCharging className="h-5 w-5 text-white/30" />
                        </div>
                        <div className="mt-4 flex items-end justify-between gap-4">
                            <div>
                                <span className="font-display text-4xl font-extrabold text-white md:text-5xl">
                                    60
                                    <span className="text-2xl text-volt md:text-3xl">
                                        HRS
                                    </span>
                                </span>
                                <p className="mt-2 max-w-[16rem] text-sm font-light leading-relaxed text-white/55">
                                    Dual GaN circuitry flashes power faster
                                    than you can finish a track.
                                </p>
                            </div>
                        </div>
                        <div className="mt-7">
                            <ChargeBar />
                        </div>
                    </Reveal>

                    <Reveal
                        data-testid="nexora-bento-item-spatial"
                        delay={0.1}
                        className={`md:col-span-12 ${cardBase}`}
                    >
                        <div className="flex flex-col gap-10 md:flex-row md:items-center">
                            <div className="md:max-w-sm">
                                <div className="flex items-center justify-between md:justify-start">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-volt/80">
                                        04 / Spatial Audio Matrix
                                    </span>
                                    <Orbit className="h-5 w-5 text-white/30 md:hidden" />
                                </div>
                                <h3 className="mt-3 font-display text-2xl font-bold text-white md:text-3xl">
                                    Sound with coordinates.
                                </h3>
                                <p className="mt-3 text-sm font-light leading-relaxed text-white/55 md:text-base">
                                    Real-time head tracking simulates a
                                    multi-channel 7.1.4 spatial field around
                                    you. Drag the field and feel the stage
                                    widen.
                                </p>
                                <div className="mt-6 flex flex-wrap gap-2">
                                    <Chip>7.1.4 Channels</Chip>
                                    <Chip>Head Tracking</Chip>
                                </div>
                            </div>
                            <SpatialWave />
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
